/**
 * @file: labels.ts
 *
 * Plain enum→Chinese label / formatter helpers shared by the per-type
 * body components. No JSX, no hooks — pure functions so the per-type
 * files don't have to drag in a primitives import just to display a
 * status name. Add a new label here only when more than one body needs
 * it; one-off labels stay co-located with their body.
 */

import { ReportContentType } from '@euler/model/ReportContentType';
import { ReportShareMethod } from '@euler/model/ReportShareMethod';
import { AbnormalLevel } from '@euler/model/enum/AbnormalLevel';
import { AccountTxType } from '@euler/model/enum/AccountTxType';
import { DeliveryCheckSceneType } from '@euler/model/enum/DeliveryCheckSceneType';
import { FundSourceType } from '@euler/model/enum/FundSourceType';
import { MaintenanceServicePlanDetailType } from '@euler/model/enum/MaintenanceServicePlanDetailType';
import { ReportOpenUserType } from '@euler/model/enum/ReportOpenUserType';
import { ReportShareTarget } from '@euler/model/enum/ReportShareTarget';
import { ServiceSubjectType } from '@euler/model/enum/ServiceSubjectType';
import { TaskFlowStatusType } from '@euler/model/enum/TaskFlowStatusType';
import { TaskFlowType } from '@euler/model/enum/TaskFlowType';

export const reportTypeName = (t: ReportContentType): string => {
  switch (t) {
    case ReportContentType.PreInspection:
      return '预检报告';
    case ReportContentType.Inspection:
      return '检测报告';
    case ReportContentType.Construction:
      return '施工报告';
    case ReportContentType.DeliveryCheck:
      return '交车报告';
    case ReportContentType.SamplingCheck:
      return '抽检报告';
    default:
      return '检测报告';
  }
};

export const shareMethodName = (m: ReportShareMethod): string => {
  switch (m) {
    case ReportShareMethod.WeixinMiniprogram:
      return '微信小程序';
    case ReportShareMethod.SmsLink:
      return '短信链接';
    case ReportShareMethod.WecomMiniprogram:
      return '企业微信小程序';
    case ReportShareMethod.AppLink:
      return '应用链接';
    default:
      return '其他方式';
  }
};

/** Who a report was shared to (customer vs the order's service agent). */
export const shareTargetName = (t: ReportShareTarget): string => {
  switch (t) {
    case ReportShareTarget.Customer:
      return '车主';
    case ReportShareTarget.ServiceAgent:
      return '服务专员';
    default:
      return '其他';
  }
};

/**
 * Who opened the report. `Customer` and `Shared` are BOTH the car
 * owner — they differ only by entry channel (`Customer` = opened from
 * the WeChat template message; `Shared` = opened via the mini-program
 * report share), so both label as 车主. `ServiceAgent` is staff;
 * `Other` is an unidentified visitor (uncommon).
 */
export const reportViewerName = (t: ReportOpenUserType): string => {
  switch (t) {
    case ReportOpenUserType.ServiceAgent:
      return '服务专员';
    case ReportOpenUserType.Other:
      return '访客';
    default:
      return '车主';
  }
};

/**
 * How the report was opened — the entry channel behind a viewer's
 * `ReportOpenUserType`. Only the two car-owner channels carry a useful
 * distinction; staff / unknown opens return undefined (no channel row).
 */
export const reportOpenChannelName = (
  t: ReportOpenUserType,
): string | undefined => {
  switch (t) {
    case ReportOpenUserType.Customer:
      return '微信模板消息';
    case ReportOpenUserType.Shared:
      return '小程序报告分享';
    default:
      return undefined;
  }
};

export const flowTypeName = (t: TaskFlowType): string => {
  switch (t) {
    case TaskFlowType.PreInspection:
      return '预检';
    case TaskFlowType.Inspection:
      return '检测';
    case TaskFlowType.InspectionFlow:
      return '检测项';
    case TaskFlowType.Construction:
      return '施工';
    case TaskFlowType.ConstructionJob:
      return '施工任务';
    case TaskFlowType.CompletionCheck:
      return '交车';
    case TaskFlowType.SamplingCheck:
      return '抽检';
    default:
      return '工单流程';
  }
};

export const flowStatusName = (s: TaskFlowStatusType): string => {
  switch (s) {
    case TaskFlowStatusType.Started:
      return '开始';
    case TaskFlowStatusType.Finished:
      return '完成';
    case TaskFlowStatusType.Cancelled:
      return '取消';
    default:
      return '更新';
  }
};

/** Short Chinese label for a plan-detail's kind (rendered as a chip). */
export const detailTypeName = (t: MaintenanceServicePlanDetailType): string => {
  switch (t) {
    case MaintenanceServicePlanDetailType.InspectionSubject:
      return '检测项';
    case MaintenanceServicePlanDetailType.ConstructionJob:
      return '施工项';
    case MaintenanceServicePlanDetailType.PendingIssue:
      return '遗留问题';
    case MaintenanceServicePlanDetailType.SolvedPendingIssue:
      return '已解决遗留';
    case MaintenanceServicePlanDetailType.FaultLikeSubject:
      return '故障项';
    case MaintenanceServicePlanDetailType.InsuranceExpiry:
      return '保险到期';
    default:
      return '自定义';
  }
};

/**
 * Type label for a catalog lead item, whose plan-detail `type` is empty and
 * whose real type lives in `subjectType` (a `ServiceSubjectType`).
 */
export const serviceSubjectTypeName = (t: ServiceSubjectType): string => {
  switch (t) {
    case ServiceSubjectType.Inspection:
      return '车辆检测';
    case ServiceSubjectType.Fault:
      return '故障检修';
    case ServiceSubjectType.Construction:
      return '维保施工';
    case ServiceSubjectType.Other:
      return '其他项目';
    default:
      return '自定义';
  }
};

/**
 * Label for a plan-detail's unsolved-reason row. Faults read "未解决原因",
 * everything else (pending issues) reads "遗留原因" — mirrors the wording
 * on the maintenance-plan item cell in the finish-task flow.
 */
export const reasonLabelFor = (t: MaintenanceServicePlanDetailType): string =>
  (t === MaintenanceServicePlanDetailType.FaultLikeSubject
    ? '未解决'
    : '遗留') + '原因';

/** Which delivery-check scene a rework was filed against. */
export const deliveryCheckSceneName = (t: DeliveryCheckSceneType): string => {
  switch (t) {
    case DeliveryCheckSceneType.CompletionCheck:
      return '竣工检验';
    case DeliveryCheckSceneType.SamplingCheck:
      return '完工抽检';
    default:
      return '交付检查';
  }
};

export const abnormalLevelInfo = (l: AbnormalLevel) => {
  switch (l) {
    case AbnormalLevel.Urgent:
      return { name: '紧急', color: '#ff3b30' };
    case AbnormalLevel.Defective:
      return { name: '异常', color: '#ff9500' };
    default:
      return { name: '正常', color: '#34c759' };
  }
};

export const fundSourceTypeName = (t: FundSourceType): string => {
  switch (t) {
    case FundSourceType.ProductAgent:
      return '商品供应商';
    case FundSourceType.OrgAccount:
      return '商户账户';
    default:
      return '资金来源';
  }
};

/**
 * Best-effort Chinese label for a known order property key on the
 * `OrgOrderInfoUpdated` diff. Falls back to the camelCase key when a
 * producer ships a new property — the client doesn't have to release
 * to render the new row, just looks ugly until we add the label.
 */
export const orderInfoFieldLabel = (key: string): string => {
  const map: Record<string, string> = {
    customerName: '车主姓名',
    customerPhone: '车主电话',
    licensePlateNo: '车牌号',
    vehicleBrandName: '品牌',
    vehicleName: '车型',
    vehicleModel: '型号',
    vin: 'VIN',
    mileage: '里程',
    serviceSubjects: '服务项目',
    expectedDeliveryAt: '预计交车时间',
    remark: '备注',
    referrerName: '推荐人',
    salesId: '销售',
    serviceAgentId: '服务顾问',
    quotedAmount: '报价',
  };
  return map[key] ?? key;
};

/** Render a primitive diff value for the order-info diff table. */
export const renderChangedValue = (
  v: string | number | boolean | null,
): string => {
  if (v == null || v === '') return '空';
  if (typeof v === 'boolean') return v ? '是' : '否';
  return String(v);
};

/** Format a cent-denominated amount as `XX.XX`. */
export const formatYuan = (cents: number): string =>
  (Math.round(cents) / 100).toFixed(2);

/**
 * Display label + accent for an account-transaction type. Mirrors the
 * mapping the account-detail page uses (`AccountTransactionsScreen`)
 * so the rewards feed message and the page read identically.
 */
export const txTypeInfo = (
  t: AccountTxType,
): { text: string; color: string } => {
  switch (t) {
    case AccountTxType.ProductReferring:
      return { text: '推荐奖励', color: '#16a34a' };
    case AccountTxType.ProductReferringShare:
      return { text: '推荐奖励 (服务顾问分成)', color: '#16a34a' };
    case AccountTxType.ProductAdoption:
      return { text: '施工奖励', color: '#1f80ff' };
    case AccountTxType.ProductAdoptionShare:
      return { text: '施工奖励 (服务顾问分成)', color: '#1f80ff' };
    case AccountTxType.ProductClosing:
      return { text: '成交奖励', color: '#7c3aed' };
    case AccountTxType.ProductClosingShare:
      return { text: '成交奖励 (服务顾问分成)', color: '#7c3aed' };
    case AccountTxType.RevokeProductReferring:
      return { text: '推荐奖励 - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeProductReferringShare:
      return { text: '推荐奖励 (服务顾问分成) - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeProductAdoption:
      return { text: '施工奖励 - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeProductAdoptionShare:
      return { text: '施工奖励 (服务顾问分成) - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeProductClosing:
      return { text: '成交奖励 - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeProductClosingShare:
      return { text: '成交奖励 (服务顾问分成) - 撤销', color: '#ff3b30' };
    case AccountTxType.CampaignReward:
      return { text: '活动奖励', color: '#fa8c16' };
    case AccountTxType.CampaignRewardShare:
      return { text: '活动奖励 (分成)', color: '#fa8c16' };
    case AccountTxType.RevokeCampaignReward:
      return { text: '活动奖励 - 撤销', color: '#ff3b30' };
    case AccountTxType.RevokeCampaignRewardShare:
      return { text: '活动奖励 (分成) - 撤销', color: '#ff3b30' };
    case AccountTxType.Withdraw:
      return { text: '余额提现', color: '#ff9500' };
    case AccountTxType.Refund:
      return { text: '账户退款', color: '#ff9500' };
    default:
      return { text: t, color: '#1f2937' };
  }
};
